/**
 * What a stored fiscal receipt can be shared and printed as — one reading of the three shapes.
 *
 * The operators return different objects and the app stores each verbatim (see
 * apps/atlas-backend/src/common/utils/fiscal-receipt.util.ts):
 *
 *   - Light Kassa: `{receipt, …}` — `receipt` is the receipt text, base64.
 *   - Wipon (current API): `{ticket, shift, check}` — `check` is printable HTML, `ticket.link` the
 *     public receipt link.
 *   - Wipon (legacy flat ticket): `{id, link, …, check}` — the link sits at the TOP level.
 *
 * Sale Detail used to inline `fiscalInfo.receipt ? decode(receipt) : fiscalInfo.ticket.link` at
 * five sites. That throws `TypeError: Cannot read property 'link' of undefined` on the legacy flat
 * shape, and on a hollow `{}` — the receipt the pre-fix backend emptied, which is still what a
 * Supabase-read till loads for those sales until the backfill restores them. A `{}` receipt is
 * still a SIGNED sale (every reader decides that by truthiness, correctly), so the screen keeps
 * showing it as fiscalized; it just has nothing to print, and now says so instead of crashing.
 */

/** Platform base64 → binary-string decoder: `atob` on web, `base-64`'s `decode` on native. */
export type Base64Decoder = (base64: string) => string

type ReceiptLike = {
  receipt?: unknown
  check?: unknown
  link?: unknown
  ticket?: unknown
} | null | undefined

const nonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0

/** Light Kassa's receipt text: base64 of UTF-8, decoded the way every call site always has. */
const lightKassaText = (receipt: string, decode: Base64Decoder): string =>
  decodeURIComponent(escape(decode(receipt)))

const ticketLink = (fiscalInfo: ReceiptLike): string | null => {
  const ticket = fiscalInfo?.ticket
  if (ticket && typeof ticket === 'object') {
    const link = (ticket as { link?: unknown }).link
    if (nonEmptyString(link)) return link
  }
  return nonEmptyString(fiscalInfo?.link) ? fiscalInfo.link : null
}

/** The text a receipt is SHARED by (WhatsApp, SMS, copy), or null when the receipt carries none. */
export function fiscalReceiptShareText(
  fiscalInfo: ReceiptLike,
  decode: Base64Decoder,
): string | null {
  if (nonEmptyString(fiscalInfo?.receipt)) return lightKassaText(fiscalInfo.receipt, decode)
  return ticketLink(fiscalInfo)
}

/** The body a receipt is PRINTED from, or null when the receipt carries none. */
export function fiscalReceiptPrintBody(
  fiscalInfo: ReceiptLike,
  decode: Base64Decoder,
): string | null {
  if (nonEmptyString(fiscalInfo?.receipt)) return lightKassaText(fiscalInfo.receipt, decode)
  return nonEmptyString(fiscalInfo?.check) ? fiscalInfo.check : null
}
